import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EMessageDirection,
  EMessageType,
  type IChatMessage,
} from '@kognitos/lattice';
import {
  AGENT_AVATAR_URL,
  AGENT_AVATAR_FALLBACK,
} from '@/shared/utils/chat-utils';
import { getCurrentDate } from '@/shared/utils/datetime-utils';
import {
  INSIGHT_TICKER_MESSAGES,
  PROGRESS_GAP_THRESHOLD_MS,
  PROGRESS_MESSAGE_INTERVAL_MS,
  PROGRESS_FAST_INTERVAL_MS,
  PROGRESS_LONG_WAIT_THRESHOLD_MS,
} from '../constants/builder-progress-messages';

/**
 * Picks a random element from an array.
 *
 * @param arr - The array to pick from
 * @returns A random element
 */
function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Creates a synthetic thinking message from the given text.
 *
 * @param text - The resolved message content
 * @returns A thinking-type IChatMessage
 */
function createSyntheticMessage(text: string): IChatMessage {
  return {
    id: `builder-progress-${Date.now()}-${Math.random()}`,
    content: text,
    direction: EMessageDirection.INCOMING,
    timestamp: getCurrentDate(),
    type: EMessageType.THINKING,
    avatarUrl: AGENT_AVATAR_URL,
    avatarFallback: AGENT_AVATAR_FALLBACK,
  };
}

/**
 * Hook that injects one synthetic thinking filler when the agent is generating
 * but no backend thinking message is currently streaming.
 *
 * The filler cycles through builder phases sequentially, mixing in insight ticker
 * messages for longer waits. The synthetic message is transient, cleared when
 * generation stops, and never persisted.
 *
 * All message text is resolved via i18n (automation namespace).
 *
 * @param isGenerating - Whether the agent is currently generating a response
 * @param realMessages - The current array of real chat messages (used to detect new messages)
 * @returns Array of synthetic thinking messages to merge into the chat
 */
export function useBuilderProgress(
  isGenerating: boolean,
  realMessages: IChatMessage[],
): IChatMessage[] {
  const { t } = useTranslation('automation');

  const [syntheticMessage, setSyntheticMessage] = useState<IChatMessage | null>(
    null,
  );

  // Use refs to avoid stale closure issues in timers
  const isGeneratingRef = useRef(isGenerating);
  isGeneratingRef.current = isGenerating;

  const tRef = useRef(t);
  tRef.current = t;

  const phaseIndexRef = useRef(0);
  const tickCountRef = useRef(0);
  const gapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generatingStartRef = useRef<number>(0);
  const latestMessageIsThinkingRef = useRef(false);

  /**
   * Picks the next progress i18n key, cycling through phases and mixing in
   * insight ticker messages every 3rd tick. Resolves the key via t().
   * Insight messages are prefixed with "Did you know?" to differentiate them
   * from progress updates.
   */
  const getNextMessage = useCallback((): string => {
    const prefix = tRef.current('automation.builderProgress.insightPrefix');
    const fact = tRef.current(pickRandom(INSIGHT_TICKER_MESSAGES).key);
    return `${prefix} ${fact}`;
  }, []);

  /**
   * Clears all timers.
   */
  const clearTimers = useCallback(() => {
    if (gapTimerRef.current) {
      clearTimeout(gapTimerRef.current);
      gapTimerRef.current = null;
    }
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Starts the recurring interval that recycles a single synthetic message.
   */
  const startTicking = useCallback(() => {
    if (intervalRef.current || !isGeneratingRef.current) return;

    setSyntheticMessage(createSyntheticMessage(getNextMessage()));

    const getInterval = () => {
      const elapsed = Date.now() - generatingStartRef.current;
      return elapsed > PROGRESS_LONG_WAIT_THRESHOLD_MS
        ? PROGRESS_FAST_INTERVAL_MS
        : PROGRESS_MESSAGE_INTERVAL_MS;
    };

    const tick = () => {
      if (!isGeneratingRef.current || latestMessageIsThinkingRef.current) {
        clearTimers();
        return;
      }

      setSyntheticMessage(createSyntheticMessage(getNextMessage()));

      intervalRef.current = setTimeout(tick, getInterval());
    };

    intervalRef.current = setTimeout(tick, getInterval());
  }, [getNextMessage, clearTimers]);

  /**
   * Schedules synthetic messages to start after the gap threshold.
   */
  const scheduleGapTimer = useCallback(() => {
    if (gapTimerRef.current) {
      clearTimeout(gapTimerRef.current);
    }

    gapTimerRef.current = setTimeout(() => {
      gapTimerRef.current = null;
      if (isGeneratingRef.current && !latestMessageIsThinkingRef.current) {
        startTicking();
      }
    }, PROGRESS_GAP_THRESHOLD_MS);
  }, [startTicking]);

  const latestMessage = realMessages[realMessages.length - 1];
  const latestMessageId = latestMessage?.id;
  const latestMessageType = latestMessage?.type;

  // eslint-disable-next-line no-restricted-syntax -- reason: Reset filler timer when backend message stream state changes
  useEffect(() => {
    clearTimers();
    setSyntheticMessage(null);

    if (!isGenerating) {
      return;
    }

    latestMessageIsThinkingRef.current =
      latestMessageType === EMessageType.THINKING;

    if (!latestMessageIsThinkingRef.current) {
      scheduleGapTimer();
    }
  }, [
    latestMessageId,
    latestMessageType,
    isGenerating,
    clearTimers,
    scheduleGapTimer,
  ]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Manage progress timer lifecycle based on generating state
  useEffect(() => {
    if (isGenerating) {
      generatingStartRef.current = Date.now();
      phaseIndexRef.current = 0;
      tickCountRef.current = 0;
      latestMessageIsThinkingRef.current =
        latestMessageType === EMessageType.THINKING;
    } else {
      clearTimers();
      setSyntheticMessage(null);
    }

    return clearTimers;
  }, [isGenerating]); // eslint-disable-line react-hooks/exhaustive-deps -- only react to isGenerating changes

  return syntheticMessage ? [syntheticMessage] : [];
}
